import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "@/components/ui/use-toast";
import { privateOfficeService } from "@/services/private-office-service";
import { extractApiErrorMessage } from "@/utils/extract-api-error-message";

const rejectSchema = z.object({
  reason: z.string().min(10, "O motivo deve ter pelo menos 10 caracteres"),
});

type RejectFormData = z.infer<typeof rejectSchema>;

export const RejectModal = () => {
  const navigate = useNavigate();
  const { requestId } = useParams<{ requestId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RejectFormData>({
    resolver: zodResolver(rejectSchema),
  });

  const handleReject = async (data: RejectFormData) => {
    if (!requestId) return;

    try {
      setIsLoading(true);
      await privateOfficeService.rejectUpdateRequest(requestId, data.reason);
      toast({
        title: "Alteração reprovada",
        description: "A alteração foi reprovada com sucesso.",
      });

      // Dispatch custom event to refresh parent data
      window.dispatchEvent(new CustomEvent("updateRequestsRefresh"));

      reset();
      navigate(-1); // Go back to the previous page
    } catch (error) {
      toast({
        title: "Erro ao reprovar",
        description: extractApiErrorMessage(
          error,
          "Ocorreu um erro ao reprovar a alteração.",
        ),
        variant: "destructive",
      });
      console.error("Error rejecting request:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      reset();
      navigate(-1); // Go back to the previous page
    }
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XMarkIcon className="w-5 h-5 text-red-600" />
            Reprovar Alteração
          </DialogTitle>
          <DialogDescription>
            Por favor, informe o motivo da reprovação desta alteração. Este
            campo é obrigatório.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleReject)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reason">Motivo da reprovação</Label>
            <Textarea
              id="reason"
              placeholder="Descreva o motivo da reprovação..."
              className="min-h-[100px] resize-none"
              {...register("reason")}
              disabled={isLoading}
            />
            {errors.reason && (
              <p className="text-sm text-red-600">{errors.reason.message}</p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={isLoading}>
              {isLoading ? "Reprovando..." : "Reprovar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
